// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

// Renderer-rule registry for Geodatabase sublayers.
//
// Each rule has a predicate `match` that runs after the GeoJSONLayer loads
// (so we know its true geometryType and fields), and an `apply` that mutates
// the layer to set renderer/elevationInfo/labels. The first matching rule wins.
//
// Rules supersede both styleConfig entries and the 2D default fallback —
// they're the most specific signal because they inspect the actual data.

import type GeoJSONLayer from '@arcgis/core/layers/GeoJSONLayer';

export interface LayerMeta {
  name: string;
  geometryType: string | null | undefined;
  fields: __esri.Field[];
}

export interface RendererRule {
  id: string;
  match: (meta: LayerMeta) => boolean;
  apply: (layer: GeoJSONLayer) => void;
  legendColor: string;
}

const hasField = (fields: __esri.Field[], name: string): boolean =>
  fields.some((f) => f.name === name);

// Known pole-bottom layer names — used as a fallback when the attribute-based
// detection misses (e.g. when GeoJSONLayer.fields hasn't been inferred yet, or
// when aw-server's GeoJSON conversion changes field casing).
const POLE_BOTTOM_NAMES = new Set(['T_UP_BOTTOM']);

// ─── Symbols ──────────────────────────────────────────────────────────────

// Cylinder primitive — used for utility poles. Esri's stock 3D libraries
// don't include a wooden utility pole, so the cylinder is the most faithful
// stand-in. Visual variables size it from Height_ft/Diameter_ft attributes.
// Declared as plain object literals (no __esri.SimpleRendererProperties annotation)
// because that interface doesn't carry the `type` discriminator; the cast on use
// in apply() does the runtime conversion via Esri's autocasting.
// Height is driven by Height_ft via Arcade IIF so the rule works whether or
// not the lambda's DB-driven rename is applied (raw `Height_ft` vs the renamed
// `Height (Ft)`). Diameter is intentionally static at 0.5 m (~1.6 ft) instead
// of using the GDB Diameter_ft attribute — real diameters are all ~0.9 ft,
// which is too thin to see at overview zoom. 0.5 m is a small bump above
// realism, still clearly pole-shaped, and visible at any reasonable zoom.
const POLE_CYLINDER_RENDERER = {
  type: 'simple',
  symbol: {
    type: 'point-3d',
    symbolLayers: [{
      type: 'object',
      resource: { primitive: 'cylinder' },
      material: { color: [168, 137, 106] },
      anchor: 'bottom',
      width: 0.5,
      depth: 0.5,
      height: 9,
    }],
  },
  visualVariables: [
    {
      type: 'size',
      axis: 'height',
      valueExpression: "IIF(IsEmpty($feature.Height_ft), $feature['Height (Ft)'], $feature.Height_ft)",
      valueUnit: 'feet',
    },
  ],
};

const POLE_LABELS: __esri.LabelClassProperties[] = [{
  symbol: {
    type: 'label-3d',
    symbolLayers: [{
      type: 'text',
      material: { color: [255, 255, 255, 1] },
      halo: { color: [0, 0, 0, 1], size: 1 },
      size: 10,
    }],
  },
  labelExpressionInfo: { expression: '$feature.Pole_Tag' },
  labelPlacement: 'center-right',
}];

// WebStyleSymbol — Esri-hosted 3D mesh, resolved at runtime from a published
// style. `EsriRealisticStreetSceneStyleLow` is a public Esri style containing
// hydrants, lamp posts, signs, etc. Browse names at
// https://developers.arcgis.com/javascript/latest/sample-code/symbols-web-style-3d/
const HYDRANT_WEBSTYLE_RENDERER = {
  type: 'simple',
  symbol: {
    type: 'web-style',
    styleName: 'EsriRealisticStreetSceneStyleLow',
    name: 'Fire_Hydrant_1',
  },
};

// Popups for GDB features are rendered by the custom React PopupWindow
// component in ../PopupWindow.tsx (mounted from ../index.tsx via the click
// hit-test handler). Don't set layer.popupTemplate here — doing so causes
// Esri's native popup to appear alongside the custom one.

// ─── Rules ────────────────────────────────────────────────────────────────

export const RENDERER_RULES: RendererRule[] = [
  {
    // Pole-like point features. Matches either:
    //  - a known pole layer name (T_UP_BOTTOM today, extend the list as needed)
    //  - OR a point layer whose schema has Height_ft + Diameter_ft (catches
    //    future pole exports without code changes)
    // The name fallback is here because GeoJSONLayer.fields can be empty or
    // case-normalized at .when() time depending on how aw-server emits the
    // GeoJSON — the attribute check alone isn't reliable.
    id: 'pole-cylinder',
    legendColor: '#A8896A',
    match: ({ name, geometryType, fields }) => {
      if (geometryType !== 'point') return false;
      if (POLE_BOTTOM_NAMES.has(name)) return true;
      return hasField(fields, 'Height_ft') && hasField(fields, 'Diameter_ft');
    },
    apply: (layer) => {
      layer.renderer = POLE_CYLINDER_RENDERER as unknown as __esri.Renderer;
      layer.elevationInfo = { mode: 'on-the-ground' };
      if (layer.fields && hasField(layer.fields, 'Pole_Tag')) {
        layer.labelingInfo = POLE_LABELS as unknown as __esri.LabelClass[];
        layer.labelsVisible = true;
      }
    },
  },
  {
    // Fire hydrants: render as the Esri Realistic Street Scene hydrant mesh.
    // Pure name match — there's no distinguishing attribute schema.
    id: 'hydrant-webstyle',
    legendColor: '#FF0000',
    match: ({ name, geometryType }) =>
      geometryType === 'point' && name === 'U_HYDRANT',
    apply: (layer) => {
      layer.renderer = HYDRANT_WEBSTYLE_RENDERER as unknown as __esri.Renderer;
      layer.elevationInfo = { mode: 'on-the-ground' };
    },
  },
];

export const findRule = (meta: LayerMeta): RendererRule | undefined =>
  RENDERER_RULES.find((r) => r.match(meta));
